import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { requestAPI } from '../../data/api'
import { withRouter } from 'react-router-dom'
import ViolationDetailCard from './ViolationDetailCard'

class DomainCounts extends Component {
	constructor(props) {
		super(props)
		
		// Get domain from URL params if available
		const urlParams = new URLSearchParams(window.location.search)
		const domainFromUrl = urlParams.get('domain') || ''
		
		this.state = {
			domain: domainFromUrl,
			domainData: [],
			loading: false,
			error: null,
			selectedCell: null, // { client, state }
			violations: [],
			loadingViolations: false,
		}
	}

	componentDidMount() {
		// If domain is pre-filled from URL, fetch data automatically
		if (this.state.domain) {
			this.fetchDomainCounts()
		}
	}

	fetchDomainCounts = async () => {
		const { domain } = this.state
		
		if (!domain || domain.trim() === '') {
			this.setState({ error: 'Please enter a domain', domainData: [] })
			return
		}

		this.setState({ loading: true, error: null })
		try {
			const response = await requestAPI(`/violations/domain/${encodeURIComponent(domain)}`, 'GET')

			// Process the response - expecting an array of objects
			const domainData = Array.isArray(response) ? response : []

			this.setState({ domainData, loading: false })
		} catch (error) {
			console.error('Failed to fetch domain counts:', error)
			this.setState({ 
				loading: false, 
				error: 'Failed to fetch domain data. Please try again.',
				domainData: []
			})
		}
	}

	handleDomainChange = (e) => {
		this.setState({ domain: e.target.value })
	}

	handleSubmit = (e) => {
		e.preventDefault()
		this.fetchDomainCounts()
	}

	fetchDomainStats = async (clientName, shortName, state) => {
		const { domain } = this.state
		
		this.setState({ 
			selectedCell: { client: clientName, state },
			loadingViolations: true,
			violations: []
		})
		
		try {
			const response = await requestAPI(
				`/violations/domainstats?shortName=${encodeURIComponent(shortName)}&state=${encodeURIComponent(state)}&domain=${encodeURIComponent(domain)}`,
				'GET'
			)
			
			const violations = Array.isArray(response) ? response : []
			this.setState({ violations, loadingViolations: false })
		} catch (error) {
			console.error('Failed to fetch domain stats:', error)
			this.setState({ 
				loadingViolations: false,
				violations: []
			})
		}
	}

	handleCellClick = (clientName, shortName, state, count) => {
		if (count > 0) {
			this.fetchDomainStats(clientName, shortName, state)
		}
	}

	// Get unique states from the data
	getStates = () => {
		const stateOrder = [
			'processing',
			'stage_01',
			'stage_02',
			'stage_03',
			'client_approval',
			'contact_mapping',
			'ready_for_export',
			'pipedrive_open',
			'pipedrive_won',
			'pipedrive_closed',
			'rejected'
		]
		
		const statesSet = new Set()
		this.state.domainData.forEach(item => {
			statesSet.add(item.state)
		})
		
		const states = Array.from(statesSet)
		
		// Sort by the defined order
		return states.sort((a, b) => {
			const indexA = stateOrder.indexOf(a)
			const indexB = stateOrder.indexOf(b)
			
			// If both states are in the order list, sort by their position
			if (indexA !== -1 && indexB !== -1) {
				return indexA - indexB
			}
			// If only A is in the list, it comes first
			if (indexA !== -1) return -1
			// If only B is in the list, it comes first
			if (indexB !== -1) return 1
			// If neither is in the list, sort alphabetically
			return a.localeCompare(b)
		})
	}

	// Get unique clients from the data
	getClients = () => {
		const clientsMap = new Map()
		this.state.domainData.forEach(item => {
			const clientKey = item.name || 'Unknown'
			if (!clientsMap.has(clientKey)) {
				clientsMap.set(clientKey, {
					name: item.name || 'Unknown',
					short_name: item.short_name || ''
				})
			}
		})
		return Array.from(clientsMap.values()).sort((a, b) => a.name.localeCompare(b.name))
	}

	// Get count for a specific client and state
	getCount = (clientName, state) => {
		const item = this.state.domainData.find(
			d => d.name === clientName && d.state === state
		)
		return item ? Number(item.count) || 0 : 0
	}

	// Get total for a state (column)
	getStateTotal = (state) => {
		return this.state.domainData
			.filter(d => d.state === state)
			.reduce((sum, item) => sum + (Number(item.count) || 0), 0)
	}

	// Get total for a client (row)
	getClientTotal = (clientName) => {
		return this.state.domainData
			.filter(d => d.name === clientName)
			.reduce((sum, item) => sum + (Number(item.count) || 0), 0)
	}

	// Get grand total
	getGrandTotal = () => {
		return this.state.domainData
			.reduce((sum, item) => sum + (Number(item.count) || 0), 0)
	}

	// Format state name for display
	formatStateName = (state) => {
		const stateNames = {
			'processing': 'Processing',
			'stage_01': 'Stage 01',
			'stage_02': 'Stage 02',
			'stage_03': 'Stage 03',
			'client_approval': 'Partner Review',
			'contact_mapping': 'Contact Mapping',
			'contact_enrichment': 'Contact Enrichment',
			'ready_for_export': 'Ready for Export',
			'pipedrive_open': 'Pipedrive Open',
			'pipedrive_won': 'Pipedrive Won',
			'pipedrive_closed': 'Pipedrive Closed',
			'rejected': 'Rejected',
		}
		return stateNames[state] || state
	}

	render() {
		const { loading, domain, domainData, error } = this.state
		const states = this.getStates()
		const clients = this.getClients()

		return (
			<div>
				<div className="statistics-controls">
					<form onSubmit={this.handleSubmit}>
						<div className="control-group">
							<label className="control-label">Domain</label>
							<div style={{ display: 'flex', gap: '10px' }}>
								<input
									type="text"
									className="control-select"
									placeholder="Enter domain (e.g., example.com)"
									value={domain}
									onChange={this.handleDomainChange}
									style={{ flex: 1 }}
								/>
								<button 
									type="submit" 
									className="btn primary"
									disabled={loading}
								>
									{loading ? 'Loading...' : 'Search'}
								</button>
							</div>
						</div>
					</form>
				</div>

				{error && (
					<div className="no-data" style={{ color: '#e74c3c', marginTop: '20px' }}>
						<p>{error}</p>
					</div>
				)}

				{loading && (
					<div className="statistics-loading">
						<div className="loading-spinner"></div>
						<p>Loading domain statistics...</p>
					</div>
				)}

				{!loading && domainData.length > 0 && (
					<div className="statistics-results">
						<h2 className="results-title">Violations for "{domain}"</h2>
						<div style={{ overflowX: 'auto' }}>
							<table className="statistics-table" style={{ minWidth: '600px' }}>
								<thead>
									<tr>
										<th style={{ position: 'sticky', left: 0, background: '#f8f9fa', zIndex: 1, textAlign: 'left' }}>Client</th>
										{states.map(state => (
											<th key={state} style={{ textAlign: 'right' }}>{this.formatStateName(state)}</th>
										))}
										<th style={{ textAlign: 'right' }}>Total</th>
									</tr>
								</thead>
								<tbody>
									{clients.map((client, index) => (
										<tr key={`${client.name}-${index}`}>
											<td style={{ position: 'sticky', left: 0, background: '#fff', fontWeight: '500' }}>
												{client.name}
												{client.short_name && (
													<span style={{ marginLeft: '8px', color: '#666', fontSize: '0.9em' }}>
														({client.short_name})
													</span>
												)}
											</td>
											{states.map(state => {
												const count = this.getCount(client.name, state)
												const isClickable = count > 0
												return (
													<td 
														key={state} 
														style={{ 
															textAlign: 'right',
															cursor: isClickable ? 'pointer' : 'default',
															color: isClickable ? '#3498db' : 'inherit'
														}}
														onClick={() => isClickable && this.handleCellClick(client.name, client.short_name, state, count)}
														onMouseOver={(e) => isClickable && (e.currentTarget.style.backgroundColor = '#f0f8ff')}
														onMouseOut={(e) => isClickable && (e.currentTarget.style.backgroundColor = 'transparent')}
													>
														{count > 0 ? count : '-'}
													</td>
												)
											})}
											<td style={{ fontWeight: '600', textAlign: 'right' }}>
												{this.getClientTotal(client.name)}
											</td>
										</tr>
									))}
								</tbody>
								<tfoot>
									<tr>
										<td className="total-label" style={{ textAlign: 'left' }}>Total</td>
										{states.map(state => (
											<td key={state} className="total-count" style={{ textAlign: 'right' }}>
												{this.getStateTotal(state)}
											</td>
										))}
										<td className="total-count" style={{ fontWeight: '700', textAlign: 'right' }}>
											{this.getGrandTotal()}
										</td>
									</tr>
								</tfoot>
							</table>
						</div>
					</div>
				)}

				{this.state.selectedCell && (
				<div style={{ marginTop: '30px', paddingBottom: '40px' }}>
					<h3 style={{ marginBottom: '15px', color: '#2c3e50' }}>
						</h3>
						
						{this.state.loadingViolations && (
							<div style={{ padding: '20px', textAlign: 'center' }}>
								<p>Loading violations...</p>
							</div>
						)}
						
						{!this.state.loadingViolations && this.state.violations.length === 0 && (
							<div style={{ padding: '20px', color: '#666' }}>
								<p>No violations found.</p>
							</div>
						)}
						
						{!this.state.loadingViolations && this.state.violations.length > 0 && (
							<div>
								<p style={{ marginBottom: '15px', color: '#666' }}>
									Found {this.state.violations.length} violation{this.state.violations.length !== 1 ? 's' : ''}
								</p>
								<div style={{
									display: 'grid',
									gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
									gap: '16px'
								}}>
									{this.state.violations.map((violation, index) => (
										<ViolationDetailCard 
											key={violation.violation_id || index}
											violation={violation}
										/>
									))}
								</div>
							</div>
						)}
					</div>
				)}

				{!loading && domainData.length === 0 && domain && !error && (
					<div className="no-data">
						<p>No violations found for domain "{domain}".</p>
					</div>
				)}
			</div>
		)
	}
}

DomainCounts.contextTypes = {
	gettext: PropTypes.func.isRequired,
}

export default withRouter(DomainCounts)
